import React from "react";

import logo from "../../assests/images/logo-dark-h.png";
import { formatMoney, type StatementSection } from "../../helpers/statement";

/**
 * The printed account statement: one section per currency, each on its own
 * wallet with its own opening and closing balance. Amounts of different
 * currencies never share a column.
 *
 * Rendered to static markup into a print window, so styling is inline. Shared
 * by the Statements page and the History dialog; it carries the registered
 * address, the regulatory status line and the compliance contact, which must
 * not differ between the two.
 */

export const HANYPAY_ADDRESS =
  "Plot # 3 Behind Home Supermarket off Dawhenya Devtraco road Community 25 estate junction GPS Address: GN-0544-8908";
export const HANYPAY_PHONE = "📞 0308048657 🌍 +233 308048657";
export const STATEMENTS_EMAIL = "myhanypaystatement@hanypay.co";
const LICENCE =
  "HanyPay Ghana Ltd — SEC Ghana Regulatory Sandbox Participant (Virtual Asset Trading Platform)";
const VERIFY =
  "For verification of the authenticity of this statement, please email our Compliance Department at compliance@hanypay.co.";

const day = (value?: string) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const stamp = (value: string) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString("sv-SE").slice(0, 16);
};

const cell = { border: "1px solid #ddd", padding: "5px 6px", fontSize: "11px", verticalAlign: "top" } as const;
const head = { ...cell, background: "#f2f2f2", textAlign: "left", fontWeight: "bold" } as const;
const num = { ...cell, textAlign: "right", whiteSpace: "nowrap" } as const;
const strong = { fontWeight: "bold" } as const;

export interface StatementDocumentProps {
  userData: any;
  sections: StatementSection[];
  startDate: string;
  endDate: string;
  /** Absolute URL: the print window is about:blank and cannot resolve a relative path. */
  logoSrc?: string;
}

function Section({ section }: { section: StatementSection }) {
  const c = section.currencyCode;
  return (
    <div style={{ marginTop: "22px", pageBreakInside: "auto" }}>
      <h3 style={{ fontSize: "14px", margin: "0 0 6px" }}>
        {c} wallet <span style={{ fontWeight: "normal", color: "#444" }}>· {section.walletNumber}</span>
      </h3>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={head}>Date</th>
            <th style={head}>Description</th>
            <th style={head}>Reference</th>
            <th style={{ ...head, textAlign: "right" }}>Debit ({c})</th>
            <th style={{ ...head, textAlign: "right" }}>Credit ({c})</th>
            <th style={{ ...head, textAlign: "right" }}>Charges ({c})</th>
            <th style={{ ...head, textAlign: "right" }}>Balance ({c})</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cell} colSpan={6}>
              <span style={strong}>Opening balance</span>
            </td>
            <td style={{ ...num, ...strong }}>{formatMoney(section.openingBalance)}</td>
          </tr>
          {section.rows.length === 0 ? (
            <tr>
              <td style={{ ...cell, textAlign: "center", color: "#555" }} colSpan={7}>
                No posted transactions in this period.
              </td>
            </tr>
          ) : (
            section.rows.map((row, i) => (
              <tr key={`${row.reference}-${i}`}>
                <td style={{ ...cell, whiteSpace: "nowrap" }}>{stamp(row.date)}</td>
                <td style={cell}>{row.description}</td>
                <td style={{ ...cell, fontSize: "10px", wordBreak: "break-all" }}>{row.reference}</td>
                <td style={num}>{row.debit ? formatMoney(row.debit) : ""}</td>
                <td style={num}>{row.credit ? formatMoney(row.credit) : ""}</td>
                <td style={num}>{row.charges ? formatMoney(row.charges) : ""}</td>
                <td style={num}>{formatMoney(row.balance)}</td>
              </tr>
            ))
          )}
          <tr>
            <td style={cell} colSpan={6}>
              <span style={strong}>Closing balance</span>
            </td>
            <td style={{ ...num, ...strong }}>{formatMoney(section.closingBalance)}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td style={{ ...cell, ...strong }} colSpan={3}>
              Totals ({section.rows.length} transaction{section.rows.length === 1 ? "" : "s"})
            </td>
            <td style={{ ...num, ...strong }}>{formatMoney(section.totalDebit)}</td>
            <td style={{ ...num, ...strong }}>{formatMoney(section.totalCredit)}</td>
            <td style={{ ...num, ...strong }}>{formatMoney(section.totalCharges)}</td>
            <td style={cell} />
          </tr>
        </tfoot>
      </table>
      {section.unpostedCount > 0 && (
        <p style={{ fontSize: "10px", color: "#555", margin: "4px 0 0" }}>
          {section.unpostedCount} pending, failed or cancelled transaction
          {section.unpostedCount === 1 ? "" : "s"} in this period {section.unpostedCount === 1 ? "is" : "are"}{" "}
          not shown; only posted transactions affect the balance.
        </p>
      )}
    </div>
  );
}

export const StatementDocument = ({ userData, sections, startDate, endDate, logoSrc }: StatementDocumentProps) => {
  const fullName = [userData?.firstname, userData?.othernames, userData?.lastname].filter(Boolean).join(" ");
  const holderAddress = [userData?.address, userData?.city, userData?.country].filter(Boolean).join(", ");

  return (
    <div
      style={{
        color: "black",
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        margin: "20px 30px 30px 20px",
        minHeight: "100vh",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <img alt="Hanypay" src={logoSrc ?? logo} height={80} width={400} />
      <div style={{ display: "flex", flexDirection: "column", fontSize: "12px", marginTop: "25px" }}>
        <p style={{ margin: 0 }}>
          <span style={strong}>Address: </span>
          {HANYPAY_ADDRESS}
        </p>
        <p style={{ margin: 0 }}>
          <span style={strong}>Phone: </span>
          {HANYPAY_PHONE}
        </p>
        <p style={{ margin: 0 }}>
          <span style={strong}>Email: </span>
          {STATEMENTS_EMAIL}
        </p>
        <p style={{ margin: 0 }}>
          <span style={strong}>Website: </span>https://hanypay.co
        </p>
      </div>

      <h2 style={{ fontSize: "18px", margin: "25px 0 8px", textAlign: "center" }}>
        HANYPAY VIRTUAL ASSET ACCOUNT STATEMENT
      </h2>
      <div style={{ display: "flex", flexDirection: "column", fontSize: "12px" }}>
        <p style={{ margin: 0 }}>
          <span style={strong}>Name: </span>
          {fullName || userData?.email}
        </p>
        {userData?.email && (
          <p style={{ margin: 0 }}>
            <span style={strong}>Email: </span>
            {userData.email}
          </p>
        )}
        {userData?.hanypay_id && (
          <p style={{ margin: 0 }}>
            <span style={strong}>Hanypay ID: </span>
            {userData.hanypay_id}
          </p>
        )}
        {holderAddress && (
          <p style={{ margin: 0 }}>
            <span style={strong}>Address: </span>
            {holderAddress}
          </p>
        )}
        <p style={{ margin: "8px 0 0" }}>
          <span style={strong}>Statement period: </span>
          {day(startDate)} to {day(endDate)}
        </p>
        <p style={{ margin: 0 }}>
          <span style={strong}>Generated: </span>
          {day(new Date().toISOString())}
        </p>
        <p style={{ margin: 0 }}>
          <span style={strong}>Currencies: </span>
          {sections.map((s) => s.currencyCode).join(", ") || "—"}
        </p>
      </div>

      {sections.map((section) => (
        <Section key={section.currencyCode} section={section} />
      ))}

      <div style={{ borderBottom: "1px solid grey", marginTop: "25px", fontSize: "11px" }}>
        <p style={{ margin: 0, ...strong }}>Notes:</p>
        <ul style={{ marginTop: "4px" }}>
          <li>Each currency is a separate wallet with its own opening and closing balance.</li>
          <li>Debit is money sent from the wallet; the sender bears any charge shown. Credit is money received.</li>
          <li>Only posted (completed) transactions are listed and affect the balance.</li>
          <li>The opening balance is the wallet balance at the start of the period; the closing balance is the balance at its end.</li>
          <li>Transactions are listed in chronological order.</li>
        </ul>
      </div>

      <p style={{ marginTop: "25px", fontSize: "12px", ...strong }}>Thank you for choosing Hanypay Ghana Limited!</p>
      <div style={{ marginTop: "10px", fontSize: "12px" }}>
        <p style={{ margin: 0 }}>For any questions or concerns regarding this statement, please contact us at:</p>
        <p style={{ margin: 0 }}>Phone: {HANYPAY_PHONE}</p>
        <p style={{ margin: 0 }}>Email: {STATEMENTS_EMAIL}</p>
      </div>
      <footer style={{ marginTop: "auto", fontSize: "11px" }}>
        <p style={strong}>{LICENCE}</p>
        <p style={{ margin: "4px 0 0" }}>{VERIFY}</p>
      </footer>
    </div>
  );
};

export default StatementDocument;
